import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { NotionDocumentSelector } from "@/components/notion/NotionDocumentSelector";

export const Route = createFileRoute("/notion-callback")({
  component: NotionCallbackPage,
});

function NotionCallbackPage() {
  const navigate = useNavigate();
  const { queryClient } = Route.useRouteContext();
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<"processing" | "selecting" | "success" | "error">(
    "selecting",  // Changed to show selector immediately
  );
  const [error, setError] = useState<string | null>(null);
  const [isProcessed, setIsProcessed] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [workspaceInfo, setWorkspaceInfo] = useState<any>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);

  useEffect(() => {
    // Prevent double processing
    if (isProcessed) return;

    const handleCallback = async () => {
      setIsProcessed(true);
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const state = urlParams.get("state");
        const error = urlParams.get("error");

        // Check for errors from Notion
        if (error) {
          const errorMessage = error === "access_denied"
            ? t("notionCallback.accessDenied", { defaultValue: "Access denied. Please allow access to continue." })
            : t("notionCallback.authorizationFailed", { defaultValue: `Notion authorization failed: ${error}` });
          throw new Error(errorMessage);
        }

        if (!code) {
          throw new Error("No authorization code received from Notion.");
        }

        if (!state) {
          throw new Error("No state parameter received from Notion.");
        }

        // Get user session for JWT token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error("Authentication required - please log in first");
        }

        // Decode state to get project ID
        const stateObj = JSON.parse(atob(state));
        const projectIdFromState = stateObj.projectId;
        setCurrentProjectId(projectIdFromState);

        // Call Supabase Edge Function to exchange code for token (modified to not auto-import)
        const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notion-oauth`;
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: "callback",
            code: code,
            state: state,
            skipAutoImport: true, // New flag to skip automatic import
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          // Handle 403 errors (document limit exceeded)
          if (response.status === 403) {
            throw new Error(data.error || "Document limit exceeded. Please upgrade your plan.");
          }
          throw new Error(data.error || "Failed to complete OAuth process");
        }

        if (!data || !data.success) {
          // Check if this is an invalid_grant error
          if (
            data?.error?.includes("invalid_grant") ||
            data?.error?.includes("만료되었거나 이미 사용")
          ) {
            throw new Error(
              "인증 코드가 만료되었거나 이미 사용되었습니다. 통합 페이지로 돌아가서 다시 연결해주세요.",
            );
          }

          throw new Error(data?.error || "Failed to complete OAuth process");
        }

        // Store the access token and show document selector
        setAccessToken(data.sessionToken);
        setWorkspaceInfo(data.workspace);
        setStatus("selecting");
      } catch (err) {
        console.error("OAuth callback error:", err);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred",
        );
        // Still show selector but with error
        setStatus("selecting");
      }
    };

    handleCallback();
  }, [navigate, queryClient, isProcessed]);

  const handleImportComplete = () => {
    // Clear import mode flag
    localStorage.removeItem("notionImportMode");

    // Navigate back to project
    if (currentProjectId) {
      navigate({
        to: "/$projectId",
        params: { projectId: currentProjectId },
      });
    } else {
      navigate({ to: "/login" });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {status === "selecting" ? (
        <NotionDocumentSelector
          projectId={currentProjectId || ""}
          accessToken={accessToken}
          workspaceInfo={workspaceInfo}
          onComplete={handleImportComplete}
          authError={error || (!currentProjectId ? t("notionCallback.noProjectError", { defaultValue: "Project ID not found" }) : null)}
        />
      ) : (
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>
              {status === "processing" && t("notionCallback.processingTitle")}
              {status === "success" && t("notionCallback.successTitle")}
              {status === "error" && t("notionCallback.errorTitle")}
            </CardTitle>
            <CardDescription>
              {status === "processing" && t("notionCallback.processingDescription")}
              {status === "success" && t("notionCallback.loadingDocuments")}
              {status === "error" && t("notionCallback.errorDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === "processing" && (
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {status === "success" && (
              <div className="text-center space-y-4">
                <div className="text-green-600">
                  <svg
                    className="w-16 h-16 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("notionCallback.redirectingIntegration")}
                </p>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-4">
                <div className="text-red-600 text-center">
                  <svg
                    className="w-16 h-16 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <Button
                  onClick={() => {
                    if (currentProjectId) {
                      navigate({
                        to: "/$projectId",
                        params: { projectId: currentProjectId },
                      });
                    } else {
                      navigate({ to: "/login" });
                    }
                  }}
                  className="w-full"
                >
                  {t("notionCallback.returnButton")}
                </Button>
              </div>
            )}

          </CardContent>
        </Card>
      )}
    </div>
  );
}